import { Client } from "@/Common/Api/Client";
import { ClientResponse } from "@/Common/Api/ClientResponse";
import { PrivateBillCostCenter } from "@/Common/Components/Billing/PrivateBillCostCenter";
import { Guid } from "@/Common/Models/Guid";
import { LabRequestType } from "@/Common/Models/Patient/LabRequest";
import * as ServerOperations from "@/Common/Operations/operations.server";

type LoadLisRisCodesResponse = {
    LISRISCodes?: LISRISCode[];
};

class LISRISCode {
    public Id: string;
    public Code: string;
    public IsRIS: boolean;
    public IsLIS: boolean;
    public Description: string;
    public Price: string;
    public CostCenterId: Guid;

    private static currentRisCodePromise: Promise<ClientResponse<LoadLisRisCodesResponse>>;

    public static async GetAll(): Promise<LISRISCode[]> {
        if (cacheStore.LISRISCodeDictionary?.any())
            return cacheStore.LISRISCodeDictionary;

        LISRISCode.currentRisCodePromise ??= Client.postAsync<LoadLisRisCodesResponse>(ServerOperations.Server.Myle_LoadLISRISCodes, {});

        const response = await LISRISCode.currentRisCodePromise;

        if (response.data?.LISRISCodes?.any())
            cacheStore.LISRISCodeDictionary = response.data.LISRISCodes;

        LISRISCode.currentRisCodePromise = null;
        return response.data.LISRISCodes;
    }

    public static GetCodesByRequestType(requestType: LabRequestType, labRequestSiteId: string): LISRISCode[] {
        if (!cacheStore.LISRISCodeDictionary)
            return []; // Assume codes have been loaded

        let costCentersForCurrentSite = cacheStore.getCostCenterBySiteId(labRequestSiteId).map(x => x.ID);

        if (requestType == LabRequestType.RIS)
            return cacheStore.LISRISCodeDictionary.filter(code => code.IsRIS && costCentersForCurrentSite.indexOf(code.CostCenterId) != -1);

        if (requestType == LabRequestType.LIS)
            return cacheStore.LISRISCodeDictionary.filter(code => code.IsLIS && costCentersForCurrentSite.indexOf(code.CostCenterId) != -1);

        return [];
    }

    public static GetCostCenterByRequestType(requestType: LabRequestType, encounterSiteId: string): PrivateBillCostCenter[] {
        if (!cacheStore.LISRISCodeDictionary)
            return [];

        let lisRisCodeFiltered = cacheStore.LISRISCodeDictionary.toLookupDictionary(y => y.CostCenterId);

        let otherFilteredLisRisCode = [];
        if (requestType === LabRequestType.LIS) {
            otherFilteredLisRisCode = lisRisCodeFiltered.filter(x => x.IsLIS).keys;
        }
        else if (requestType === LabRequestType.RIS) {
            otherFilteredLisRisCode = lisRisCodeFiltered.filter(x => x.IsRIS).keys;
        }

        return cacheStore.CostCenterCache.filter(x => x.SiteId === encounterSiteId && otherFilteredLisRisCode.some(costCenterId => costCenterId === x.ID));
    }
}

export { LISRISCode };
